/**
 * MenuItem Component
 *
 * Individual menu item display component.
 * Shows coffee item with circular image, name, description, and price.
 *
 * Props:
 * @param imgSrc - Path to item image (must start with '/')
 * @param name - Item name (displayed in uppercase)
 * @param description - Item description text
 * @param price - Item price (formatted to 2 decimal places)
 */

import { Image } from "@heroui/image";

type PropsType = {
  imgSrc: `/${string}`; // Template literal type: string starting with '/'
  name: string;
  description: string;
  price: number;
};

const MenuItem = ({ imgSrc, name, description, price }: PropsType) => {
  return (
    <div className="flex items-center gap-4">
      {/* Circular Item Image */}
      <div className="relative w-15 h-15 xl:w-18 xl:h-18 rounded-full">
        <Image
          alt=""
          className="object-cover" // Covers circle while maintaining aspect ratio
          src={imgSrc}
        />
      </div>

      {/* Item Text Content */}
      <div className="flex flex-col flex-1 gap-2">
        {/* Name, Dashed Line, Price Row */}
        <div className="flex justify-between gap-4 items-baseline">
          {/* Item Name - Uppercase, bold */}
          <p className="uppercase font-primary font-semibold text-[22px] leading-none text-primary">
            {name}
          </p>
          {/* Decorative Dashed Border - Fills remaining space */}
          <div className="flex-1 border-b border-dashed border-primary" />
          {/* Price - Large, bold, formatted to 2 decimal places */}
          <p className="leading-none font-primary font-semibold text-[30px] text-primary">
            {price.toFixed(2)}
          </p>
        </div>
        {/* Item Description */}
        <p>{description}</p>
      </div>
    </div>
  );
};

export default MenuItem;
